import { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

const MARGIN = { top: 15, right: 40, bottom: 15, left: 50 };

export const Historic = ({ histo }) => {
  const containerRef = useRef();
  const svgRef = useRef();
  const [tooltip, setTooltip] = useState(null);

  const points = histo.data.map((item) => ({
    x: `${item.saison} (P${item.phase})`,
    y: parseInt(item.point, 10),
  }));

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const draw = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      const innerW = width - MARGIN.left - MARGIN.right;
      const innerH = height - MARGIN.top - MARGIN.bottom;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();
      svg.attr("width", width).attr("height", height);

      const g = svg
        .append("g")
        .attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

      const xScale = d3
        .scalePoint()
        .domain(points.map((d) => d.x))
        .range([0, innerW]);

      const yMin = d3.min(points, (d) => d.y);
      const yMax = d3.max(points, (d) => d.y);
      const yPad = (yMax - yMin) * 0.15 || 50;

      const yScale = d3
        .scaleLinear()
        .domain([yMin - yPad, yMax + yPad])
        .range([innerH, 0]);

      // grid + Y axis
      g.append("g")
        .call(d3.axisLeft(yScale).ticks(5).tickSize(-innerW).tickPadding(10))
        .call((ax) => ax.select(".domain").remove())
        .call((ax) =>
          ax
            .selectAll(".tick line")
            .attr("stroke", "#ccc")
            .attr("stroke-width", 1),
        )
        .call((ax) =>
          ax.selectAll(".tick text").attr("font-size", 13).attr("fill", "#555"),
        );

      // vertical grid lines
      g.append("g")
        .attr("transform", `translate(0,${innerH})`)
        .call(d3.axisBottom(xScale).tickSize(-innerH).tickFormat(""))
        .call((ax) => ax.select(".domain").remove())
        .call((ax) =>
          ax
            .selectAll(".tick line")
            .attr("stroke", "#ccc")
            .attr("stroke-width", 1),
        );

      // line
      const line = d3
        .line()
        .x((d) => xScale(d.x))
        .y((d) => yScale(d.y));

      g.append("path")
        .datum(points)
        .attr("fill", "none")
        .attr("stroke", "#ee3962")
        .attr("stroke-width", 2.5)
        .attr("d", line);

      // dots
      const dots = g
        .selectAll("circle")
        .data(points)
        .join("circle")
        .attr("cx", (d) => xScale(d.x))
        .attr("cy", (d) => yScale(d.y))
        .attr("r", 5)
        .attr("fill", "#fff")
        .attr("stroke", "#20296a")
        .attr("stroke-width", 2);

      // crosshair
      const crosshairG = g
        .append("g")
        .style("display", "none")
        .style("pointer-events", "none");

      const crosshairX = crosshairG
        .append("line")
        .attr("y1", 0)
        .attr("y2", innerH)
        .attr("stroke", "#20296a")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "5,3");

      const crosshairY = crosshairG
        .append("line")
        .attr("x1", 0)
        .attr("x2", innerW)
        .attr("stroke", "#20296a")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "5,3");

      // overlay transparent pour capter la souris
      g.append("rect")
        .attr("width", innerW)
        .attr("height", innerH)
        .attr("fill", "transparent")
        .style("cursor", "crosshair")
        .on("mousemove", function (event) {
          const [mx] = d3.pointer(event);

          const nearest = points.reduce((prev, curr) =>
            Math.abs(xScale(curr.x) - mx) < Math.abs(xScale(prev.x) - mx)
              ? curr
              : prev,
          );

          const nx = xScale(nearest.x);
          const ny = yScale(nearest.y);

          crosshairG.style("display", null);
          crosshairX.attr("x1", nx).attr("x2", nx);
          crosshairY.attr("y1", ny).attr("y2", ny);

          dots.attr("r", (d) => (d === nearest ? 8 : 5));

          setTooltip({
            xPct: ((nx + MARGIN.left) / width) * 100,
            yPct: ((ny + MARGIN.top) / height) * 100,
            data: nearest,
          });
        })
        .on("mouseleave", function () {
          crosshairG.style("display", "none");
          dots.attr("r", 5);
          setTooltip(null);
        });
    };

    draw();

    const ro = new ResizeObserver(() => {
      setTooltip(null);
      draw();
    });
    ro.observe(container);

    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(points)]);

  return (
    <div ref={containerRef} className="histo" style={{ position: "relative" }}>
      <svg ref={svgRef} />
      {tooltip && (
        <div
          className="tooltip-graph"
          style={{
            position: "absolute",
            left: `${tooltip.xPct}%`,
            top: `${tooltip.yPct}%`,
            transform: "translate(-50%, calc(-100% - 10px))",
            pointerEvents: "none",
          }}
        >
          {tooltip.data.x.slice(7)}
          <br />
          {tooltip.data.y} pts
        </div>
      )}
    </div>
  );
};
